import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface SheetProps {
  open: boolean;
  /** Escape closes the sheet when given. Sheets with no way out (a decision) omit it. */
  onClose?: () => void;
  /** Heading of the sheet (rendered as h2.q.q--sm), also its accessible name. */
  title: ReactNode;
  children: ReactNode;
}

/**
 * Bottom sheet over the current screen (the denied-boarding gate on S6, the S11 handoff,
 * S12's add-a-passenger). Modal: focus moves into it and returns when it closes.
 */
export function Sheet({ open, onClose, title, children }: SheetProps) {
  const titleId = useId();
  const card = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    const first = card.current?.querySelector<HTMLElement>('button, input, textarea, select, a[href]');
    (first ?? card.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="sheet">
      <div
        className="sheet__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={card}
        tabIndex={-1}
      >
        <h2 className="q q--sm" id={titleId}>
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}
